import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, Save, BarChart3, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/utils/datetime";
import { ExamNotifyActions } from "@/components/exams/ExamNotifyActions";

type Result = {
    id: string; student_id: string; student_name: string | null;
    score: number | null; status: string; note: string | null;
};

export default function ExamDetail() {
    const { id } = useParams();
    const { t } = useTranslation();
    const nav = useNavigate();
    const { hasPerm } = useAuth();
    const [exam, setExam] = useState<any>(null);
    const [results, setResults] = useState<Result[]>([]);
    const [analytics, setAnalytics] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [tab, setTab] = useState<"results" | "analytics">("results");
    // Phase COMM-1: short-lived banner surfaced right below the save
    // button. Null when there's nothing to say (initial state, or a save
    // that didn't queue any emails).
    const [notifyMsg, setNotifyMsg] = useState<string | null>(null);
    const [notifyKind, setNotifyKind] = useState<"info" | "warning">("info");
    const canEdit = hasPerm("exam.edit");

    const load = async () => {
        setLoading(true); setErr(null);
        try {
            const [d, a] = await Promise.all([
                api.get(`/exams/${id}`),
                api.get(`/exams/${id}/analytics`).catch(() => ({ data: null })),
            ]);
            setExam(d.data.exam); setResults(d.data.results); setAnalytics(a.data);
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

    const submit = async () => {
        setSaving(true); setErr(null); setNotifyMsg(null);
        try {
            const payload = { results: results.map((r) => ({ student_id: r.student_id, score: r.score, status: r.status === "absent" ? "absent" : undefined, note: r.note })) };
            const { data } = await api.post(`/exams/${id}/results`, payload);
            toast.success(t("exams.results_saved"));
            // Phase COMM-1: surface a small banner reflecting the guardian
            // email pipeline outcome. Never spam the operator: only speak
            // up when something actually happened for guardians.
            const n = data?.email_notifications as {sent:number; failed:number; skipped:number; guardians:number} | undefined;
            if (n && n.guardians > 0) {
                if (n.failed > 0) {
                    setNotifyKind("warning");
                    setNotifyMsg(t("exams.email.partial_failure", { defaultValue: "Bəzi valideynlərə email göndərilmədi ({{failed}}). Ətraflı üçün audit tarixçəsinə baxın.", failed: n.failed }));
                } else if (n.sent > 0) {
                    setNotifyKind("info");
                    setNotifyMsg(t("exams.email.sent", { defaultValue: "{{sent}} valideynə email göndərildi.", sent: n.sent }));
                } else if (n.skipped > 0) {
                    setNotifyKind("info");
                    setNotifyMsg(t("exams.email.skipped", { defaultValue: "Yeni dəyişiklik olmadığı üçün email göndərilmədi." }));
                }
            }
            load();
        } catch (e: any) {
            const detail = e?.response?.data?.detail;
            const code = typeof detail === "object" ? detail?.error : null;
            if (code === "score_out_of_range") setErr(t("exams.err.score_range", { max: exam?.max_score }));
            else if (code === "teacher_out_of_scope") setErr(t("exams.err.teacher_scope"));
            else setErr(formatApiError(e));
        } finally { setSaving(false); }
    };

    if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>;
    if (err && !exam) return <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>;
    if (!exam) return null;

    return (
        <div className="space-y-6" data-testid="exam-detail">
            <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm" style={{ color: "var(--text-muted)" }} data-testid="back-btn"><ArrowLeft size={14} /> {t("common.back")}</button>
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">{exam.title}</h1>
                    <div className="flex flex-wrap gap-2 mt-2 text-xs" style={{ color: "var(--text-muted)" }}>
                        <span className="status-pill status-info">{t(`exams.type.${exam.type}` as any)}</span>
                        <span>{fmtDateTime(exam.scheduled_at)}</span>
                        <span>{t("exams.max_score")}: {exam.max_score}</span>
                        <span>{t("exams.pass_score")}: {exam.pass_score}</span>
                        {/* Phase UI-2: `weight` is no longer surfaced. */}
                    </div>
                </div>
                {canEdit && <ExamNotifyActions examId={exam.id} />}
            </div>
            <div className="flex gap-1 border-b" style={{ borderColor: "var(--border)" }}>
                <button data-testid="exam-tab-results" onClick={() => setTab("results")} className="px-4 py-2 text-sm border-b-2" style={{ color: tab === "results" ? "var(--text)" : "var(--text-muted)", borderColor: tab === "results" ? "var(--primary)" : "transparent" }}>{t("exams.tab.results")}</button>
                <button data-testid="exam-tab-analytics" onClick={() => setTab("analytics")} className="px-4 py-2 text-sm border-b-2" style={{ color: tab === "analytics" ? "var(--text)" : "var(--text-muted)", borderColor: tab === "analytics" ? "var(--primary)" : "transparent" }}>{t("exams.tab.analytics")}</button>
            </div>
            {tab === "results" && (
                <div className="space-y-3" data-testid="exam-results">
                    <div className="surface-card overflow-hidden">
                        <table className="w-full text-sm">
                            <thead><tr className="text-left" style={{ background: "var(--input)", color: "var(--text-muted)" }}>
                                <th className="px-4 py-2 font-medium">{t("common.name")}</th>
                                <th className="px-4 py-2 font-medium">{t("exams.score")}</th>
                                <th className="px-4 py-2 font-medium">{t("common.status")}</th>
                                <th className="px-4 py-2 font-medium">{t("exams.note")}</th>
                            </tr></thead>
                            <tbody>{results.map((r, i) => (
                                <tr key={r.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`result-row-${r.student_id}`}>
                                    <td className="px-4 py-2" style={{ color: "var(--text)" }}>{r.student_name || r.student_id.slice(0, 8)}</td>
                                    <td className="px-4 py-2">
                                        <input data-testid={`result-score-${r.student_id}`} disabled={!canEdit || r.status === "absent"} type="number" min={0} max={exam.max_score} value={r.score ?? ""} onChange={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); setResults(results.map((x, j) => j === i ? { ...x, score: v } : x)); }} className="surface-input px-2 py-1 w-24 text-sm" style={{ minHeight: 34 }} />
                                    </td>
                                    <td className="px-4 py-2">
                                        {canEdit ? (
                                            <label className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--text)" }}>
                                                <input type="checkbox" data-testid={`result-absent-${r.student_id}`} checked={r.status === "absent"} onChange={(e) => setResults(results.map((x, j) => j === i ? { ...x, status: e.target.checked ? "absent" : "pending", score: e.target.checked ? null : x.score } : x))} />
                                                {t("exams.status.absent")}
                                            </label>
                                        ) : <span className="status-pill status-neutral">{t(`exams.status.${r.status}` as any, { defaultValue: r.status })}</span>}
                                        {canEdit && r.status !== "absent" && <span className="ml-2 status-pill status-info">{t(`exams.status.${r.status}` as any, { defaultValue: r.status })}</span>}
                                    </td>
                                    <td className="px-4 py-2">
                                        <input data-testid={`result-note-${r.student_id}`} disabled={!canEdit} value={r.note || ""} onChange={(e) => setResults(results.map((x, j) => j === i ? { ...x, note: e.target.value } : x))} className="surface-input px-2 py-1 w-full text-sm" style={{ minHeight: 34 }} />
                                    </td>
                                </tr>
                            ))}</tbody>
                        </table>
                    </div>
                    {err && <div className="text-sm rounded-md p-2" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="exam-save-err">{err}</div>}
                    {notifyMsg && (
                        <div data-testid="exam-email-notify" className="text-sm rounded-md p-2"
                            style={{ background: notifyKind === "warning" ? "var(--warning-bg)" : "var(--info-bg)",
                                     color: notifyKind === "warning" ? "var(--warning-fg)" : "var(--info-fg)" }}>
                            {notifyMsg}
                        </div>
                    )}
                    {canEdit && (
                        <button data-testid="exam-save-results-btn" disabled={saving} onClick={submit} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {t("common.save")}
                        </button>
                    )}
                </div>
            )}
            {tab === "analytics" && analytics && (
                <div className="space-y-4" data-testid="exam-analytics">
                    {/* Phase UI-2: dropped the `weighted_avg` KPI; the
                        backend still returns it for API consumers but the
                        tenant UI now shows just avg / pass_rate / max. */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <Kpi label={t("exams.stats.avg")} value={analytics.avg ?? "—"} testid="stat-avg" />
                        <Kpi label={t("exams.stats.pass_rate")} value={analytics.pass_rate_pct != null ? `${analytics.pass_rate_pct}%` : "—"} testid="stat-pass" />
                        <Kpi label={t("exams.stats.max")} value={analytics.max ?? "—"} testid="stat-max" />
                    </div>
                    <div className="surface-card overflow-hidden" data-testid="exam-ranking">
                        <div className="px-4 py-2 border-b text-sm flex items-center gap-2" style={{ borderColor: "var(--border)", color: "var(--text)" }}>
                            <Trophy size={14} style={{ color: "var(--accent)" }} /> {t("exams.stats.ranking")}
                        </div>
                        {analytics.ranking.length === 0 ? <div className="p-6 text-sm text-center" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div> :
                            <table className="w-full text-sm"><tbody>{analytics.ranking.map((r: any) => (
                                <tr key={r.student_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`rank-${r.rank}`}>
                                    <td className="px-4 py-2 tabular-nums" style={{ color: "var(--text-muted)" }}>#{r.rank}</td>
                                    <td className="px-4 py-2" style={{ color: "var(--text)" }}>{r.student_name}</td>
                                    <td className="px-4 py-2 tabular-nums text-right" style={{ color: "var(--text)" }}>{r.score}</td>
                                    <td className="px-4 py-2"><span className={`status-pill status-${r.status === "passed" ? "success" : r.status === "failed" ? "error" : "neutral"}`}>{t(`exams.status.${r.status}` as any, { defaultValue: r.status })}</span></td>
                                </tr>
                            ))}</tbody></table>}
                    </div>
                </div>
            )}
        </div>
    );
}

function Kpi({ label, value, testid }: { label: string; value: any; testid: string }) {
    return <div className="surface-card p-4" data-testid={testid}>
        <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>{label}</div>
        <div className="text-2xl font-semibold tabular-nums" style={{ color: "var(--text)" }}>{value}</div>
    </div>;
}
